import { ButtonType } from "antd/lib/button";
import { useMemo } from "react";
import { createUseStyles } from "react-jss";
import { useHistory } from "react-router";

import { withdrawAsset } from "../../../api/withdraw";
import { formatDecimal } from "../../../helpers/format";
import { gte, lt, lte, sub } from "../../../helpers/numbers";
import { hashWithdrawRequest } from "../../../helpers/requestHashers";
import { IValidator, useTokenInputState } from "../../../helpers/tokenAmountState";
import { useTransitionOperator } from "../../../hooks/operation/transitionOperator";
import { useWeb3Context } from "../../../providers/Web3ContextProvider";
import { Theme } from "../../../theme/theme";
import LabelWithPopover from "../../LabelWithPopover";
import { ITokenInputChangeEvent } from "../../TokenInput";
import ActionModal from "../common/ActionModal";
import ActionTitle from "../common/ActionTitle";
import ModalExtraInfoRow from "../common/ModalExtraInfoRow";
import ModalResult from "../common/ModalResult";
import ModalTokenInput from "../common/ModalTokenInput";
import { DWModalProps } from "./DepositModal";

const useStyles = createUseStyles((theme: Theme) => ({
  heighlight: theme.highlightedText,
}));

export default function WithdrawModal({ token, onClose }: DWModalProps): JSX.Element {
  const classes = useStyles();

  const history = useHistory();
  const { address } = useWeb3Context();

  const minWithdraw = useMemo(() => formatDecimal(token.minWithdraw, token.decimal, token.decimal), [token]);
  const validators: IValidator[] = useMemo(
    () => [
      {
        validator: amtBig => gte(amtBig, token.minWithdraw),
        message: `Please input a number larger than ${minWithdraw}`,
      },
      {
        validator: amtBig => gte(amtBig, token.withdrawFee?.amount),
        message: `Please input a number larger than the fee`,
      },
      {
        validator: amtBig => lte(amtBig, token.amount),
        message: `You don’t have enough L2 balance to withdraw`,
      },
    ],
    [minWithdraw, token],
  );
  const [amount, setAmount] = useTokenInputState(token.decimal, undefined, validators);

  const { operation, signAndOperate, resetOperationState } = useTransitionOperator(withdrawAsset, hashWithdrawRequest);

  const handleAction = async () => {
    signAndOperate({
      receiver: address.toLowerCase(),
      assetId: token.id,
      assetAmount: amount.big.toString(),
      timestamp: Date.now(),
      fee: token.withdrawFee?.amount as string,
      signature: "",
    });
  };

  const handleGoToHistory = () => {
    history.push("/history");
  };

  const handleTokenInputChange = (e: ITokenInputChangeEvent) => {
    setAmount(e.value);
  };

  const handleModalClose = () => {
    resetOperationState();
    onClose();
  };

  const withdrawAmountRow = (
    <ModalExtraInfoRow key={1} left="Withdraw Amount" right={`${amount.formatted} ${token.symbol}`} />
  );
  const feeRow = (
    <ModalExtraInfoRow
      key={2}
      left={
        <LabelWithPopover label="Fee">
          This is the fee charged to cover the operation cost for this transaction.
        </LabelWithPopover>
      }
      right={`${formatDecimal(token.withdrawFee?.amount, token.decimal)} ${token.symbol}`}
    />
  );
  const youReceiveRow = (
    <ModalExtraInfoRow
      key={3}
      left={
        <LabelWithPopover label="You Receive on L1">
          This is the {token.symbol} you will receive in your L1 wallet after the fee is deducted.
        </LabelWithPopover>
      }
      right={`${
        lt(sub(amount.big, token.withdrawFee?.amount), 0)
          ? 0
          : formatDecimal(sub(amount.big, token.withdrawFee?.amount), token.decimal)
      } ${token.symbol}`}
    />
  );

  let content = (
    <ModalTokenInput
      description="You can withdraw your available L2 balance to your L1 wallet."
      amount={amount.input}
      maxAmount={formatDecimal(token.amount, token.decimal)}
      symbol={token.symbol}
      onChange={handleTokenInputChange}
      bottomDescription={`Minimal Withdraw Amount: ${minWithdraw} ${token.symbol}`}
    />
  );
  let action: () => void;
  let actionText: string;
  let buttonType: ButtonType;
  let extra;

  if (operation.completed) {
    content = (
      <ModalResult
        title="Withdrawal has been submitted"
        description={
          <span>
            In order to receive the fund in your L1 wallet, you will need to{" "}
            <span className={classes.heighlight}>manually confirm</span> the withdrawal again{" "}
            {process.env.REACT_APP_WITHDRAW_WINDOW}
            later in the history page.
          </span>
        }
      />
    );
    action = handleGoToHistory;
    actionText = "Check History";
    buttonType = "link";
    extra = [withdrawAmountRow, feeRow, youReceiveRow];
  } else {
    action = handleAction;
    actionText = "Withdraw";
    buttonType = "primary";
    extra = [feeRow, youReceiveRow];
  }

  return (
    <ActionModal
      visible
      title={<ActionTitle title="Withdraw to L1" token={token} />}
      actionText={actionText}
      actionDisabled={!amount.input}
      errMsg={amount.error}
      onCancel={handleModalClose}
      onAction={action}
      buttonType={buttonType}
      actionLoading={operation.loading}
      extra={extra}
    >
      {content}
    </ActionModal>
  );
}
